import i18n from '@/libs/i18n'

export enum Errors {
    InternalServerError = 1,
    InvalidData         = 2,
    NotFound            = 3,
    Unauthorized        = 4,
    FailedToInsert      = 5,
    ItemAlreadyExists   = 6,
    Forbidden           = 7,
    IncorrectStatus     = 8,
    /**
     * The same confirmed entry is already being carried out — or was interrupted
     * halfway. Never taken over automatically: an interrupted minibar entry may
     * already have charged the guest and taken the bottle off the shelf.
     */
    OperationInProgress = 11,
    /** One entry name arriving with different contents: the screen is stale. */
    OperationMismatch   = 12,
    /**
     * The room moved between this screen reading it and pressing the button.
     *
     * It shares a 409 with IncorrectStatus and is not the same thing: IncorrectStatus
     * is "the room cannot go there from where it is", and this is "your change did not
     * happen, because somebody else got there first". The second needs a reload before
     * the person tries again; the first does not.
     */
    StateMovedOn        = 14,
    /**
     * A departure somebody tried to start while its guest is still in the room.
     *
     * It shares 409 with IncorrectStatus and is not the same thing: nothing is wrong.
     * Checking out and leaving the room are two moments (docs/HOUSEKEEPING.md §16), and
     * the room becomes hers the moment somebody says the key is back — so the screen
     * says that rather than "wrong state", which sends a person looking for a fault.
     */
    RoomNotReleased     = 29,
    /**
     * A room somebody else is standing in.
     *
     * It shares 409 with IncorrectStatus and means something else entirely: the move IS
     * legal from where the task is, it is simply not this person's to make. Its own code
     * so a screen can say who has the room rather than "wrong state".
     */
    RoomInProgressByOther = 21,
    /**
     * A room somebody marked for cleaning that is out of order or out of service.
     *
     * It shares 409 with IncorrectStatus and says something a supervisor can act on that
     * "wrong state" does not: the room is closed, and the way in is to reopen it.
     */
    RoomOutOfService    = 32,
    /**
     * A room marked for cleaning that is already on today's round.
     *
     * Barely a refusal: what the person asked for is already true. The body carries the
     * task — kind, status, whoever holds it — so the screen shows the work instead of a
     * no (CLAUDE.md deviation 14).
     */
    HousekeepingTaskAlreadyOpen = 33,
    /**
     * A route of a module this hotel has not bought.
     *
     * It shares 403 with Forbidden and is not the same thing: Forbidden is "this
     * account may not", which another account or another right can fix, and this is
     * "this house has not got the module", which nobody signed in can fix at all. The
     * code is the only thing that tells them apart (docs/MODULI.md §3.2), and the two
     * have completely different ways out — so the portal stops on this one instead of
     * showing a toast beside an empty screen.
     */
    ModuleNotLicensed   = 19,
    /**
     * The two ways a turn of the assistant ends with nobody having refused anything
     * (docs/AI.md §31): it used its whole budget, or the process working it out went
     * away mid-answer — a deploy, a restart.
     *
     * Their own codes because the portal ACTS differently on each. One says „vprašaj
     * ožje“, the other says „vprašaj znova, prekinilo se je“, and the generic code would
     * print "something went wrong" over both — which is the sentence that teaches people
     * to reload, and reloading used to be how an answer was thrown away.
     */
    AssistantTurnTimedOut = 95,
    AssistantTurnLost     = 96,
    /**
     * A person caught between a team and a room she is holding alone — one rule read
     * from two ends (docs/HOUSEKEEPING.md §15, decision 4).
     *
     * It shares 409 with IncorrectStatus and needs its own code because the refusal
     * carries an object somebody acts on rather than a sentence somebody reads: who,
     * and either the team she is already in or the rooms that have to be moved off her
     * first. See teamConflictMessage in @/libs/housekeeping.
     */
    HousekeepingTeamConflict = 27,
    /**
     * A morning too thin for what the house marked its rooms as worth: an apartment
     * marked for three, and two people free (docs/HOUSEKEEPING.md §15, decision 7).
     *
     * It shares 409 with IncorrectStatus and needs its own code for the same reason the
     * one above does: the refusal carries a list somebody acts on — which rooms asked,
     * and how many people there are — and the ways out are the supervisor's own. See
     * teamShortfallMessage in @/libs/housekeeping.
     */
    HousekeepingTeamShortfall = 28,
    /**
     * A hand-set round order naming a room that is not on that round today
     * (docs/HOUSEKEEPING.md §5.1.4).
     *
     * It shares 409 with IncorrectStatus and is not a mistake: the order was true when
     * the timeline drew it, and the ordinary cause is a room finished between the
     * drawing and the drop. Its own code so the card can say "krog se je spremenil" and
     * reload itself, rather than reporting that the request was wrong.
     */
    HousekeepingOrderMismatch = 38,
    /**
     * A stamp, a correction or an absence for a day this person has no work period
     * covering (docs/OSEBJE.md §4).
     *
     * It shares 403 with Forbidden and is not the same thing: nothing about the caller's
     * rights is wrong, and no right anybody could be given would help — the personnel
     * card is what is missing, and somebody else fixes it. Its own sentence rather than
     * the service's, for the reason RoomInProgressByOther has one: the API's message is
     * English, and this portal is read on a telephone in a corridor.
     */
    StaffNotEmployed    = 39,
    /**
     * The other six of the working-time record (docs/EVIDENCA-CASA.md §9).
     *
     * They are here for the reason StaffNotEmployed is, and they were added the same
     * morning it was found on a telephone: an unmapped code falls to `body.message`,
     * which for every one of these is the English of `pkg/InternalErrors` — "this stamp
     * does not follow the day as it stands" — and this portal is read on a telephone in
     * a corridor. The sentences match the two iOS applications word for word
     * (HousekeepingVocabulary.refusalSentence), because the telephone, the wrist and
     * this screen must not call one refusal by three names.
     *
     * 44 and 45 are the kiosk's PINs and no screen of this portal can provoke them
     * today. They are mapped anyway: a code nobody mapped is a code that reaches
     * somebody in English.
     */
    StaffTimeSequence      = 40,
    StaffTimeOutOfRange    = 41,
    StaffBreakRecordingOff = 42,
    StaffSelfDecision      = 43,
    StaffPINAmbiguous      = 44,
    StaffPINThrottled      = 45,
    /**
     * A group's held room given up after somebody has already been put into it.
     *
     * It shares 409 with IncorrectStatus and is not a stale id: the room is an ordinary
     * reservation now, and it is cancelled where reservations are cancelled.
     */
    GroupHoldPickedUp      = 46,
    /**
     * A quantity prepared at one scale and sent after the article had moved to another
     * (docs/MATERIALNO.md §9g).
     *
     * It shares 409 with IncorrectStatus and is not the same thing, and the difference
     * is the whole reason it exists: nothing is in the wrong state and the figure the
     * person typed is the one they meant — what moved is the ARTICLE, between the
     * moment this form was drawn and the moment it was sent. A screen that could not
     * tell the two apart would offer Retry, and a retry of the same body books the same
     * wrong quantity.
     *
     * The trolley is the path it matters most on: this form sits open for a shift. No
     * screen branches on this code any more, and none may: the refusal CLOSES the
     * operation and says so itself, which `libs/operation.ts` turns into
     * `outcome.closed`. What is left here is the SENTENCE, which is a different
     * question.
     */
    StockScaleChanged      = 113,
}

/**
 * The API's error CODE, or 0 where the body carries none.
 *
 * For the callers that need to branch rather than print — the assistant's proposal card
 * tells "already decided" (IncorrectStatus, the operation was settled by an earlier
 * press) apart from a genuine refusal, because retrying the first would answer exactly
 * the same way.
 */
export const parseErrorCode = (errorObject: unknown): number =>
    (errorObject as { error?: { code?: number } })?.error?.code ?? 0

/**
 * Turns the API's {"error": {"code": n, "message": …}} body into a message for the
 * desk. Reads through i18n.global so it works outside a setup context too.
 *
 * The server's own words win by default — see pms-web's libs/error.ts for the full
 * account (found live, 2026-09-03: ItemAlreadyExists and IncorrectStatus answered
 * only the caller's fallback or the generic sentence, discarding a precise reason
 * the API had already given). Both now read body.message first, and so does the
 * catch-all default. The codes with their own fixed translated sentence
 * (StateMovedOn, OperationInProgress, OperationMismatch, ModuleNotLicensed) are
 * unchanged — each has its own comment saying why.
 */
export const parseError = (errorObject: unknown, fallback = ''): string => {
    const t = i18n.global.t
    const raw = (errorObject as { error?: unknown })?.error

    // A bare-string body (PrintHTTP.WriteErrorMessage: {"error": "..."}) carries no
    // code for anything below to key on — see pms-web's libs/error.ts for the fuller
    // account (found live 2026-09-03: a decode failure answered a 400 this way, and
    // the toast said only "could not save").
    if (typeof raw === 'string') return raw || fallback || t('errors.generic_error')

    const body = raw as { code?: number, message?: string } | undefined
    const code = body?.code

    if (!code) return fallback || t('errors.generic_error')

    switch (code) {
        case Errors.InvalidData:       return t('errors.invalid_data')
        case Errors.NotFound:          return t('errors.not_found')
        case Errors.Unauthorized:      return t('errors.unauthorized')
        case Errors.Forbidden:         return t('errors.unauthorized')
        case Errors.ItemAlreadyExists: return body?.message || fallback || t('errors.conflict')
        case Errors.IncorrectStatus:   return body?.message || fallback || t('errors.generic_error')
        // Its own sentence rather than the caller's fallback: "the action failed" is
        // true and useless, and what the person needs to know is that somebody else
        // moved the room and the screen has caught up.
        case Errors.StateMovedOn:      return t('errors.state_moved_on')
        // Its own sentence rather than the service's. The message the API sends for this
        // one is English and names the person after a colon; this portal is read on a
        // telephone in a corridor by people who do not read English, and prising the
        // name back out of the prose is the parsing CLAUDE.md deviation 14 exists to
        // stop. The room is on the screen either way — what the refusal has to say is
        // that it is not free.
        case Errors.RoomInProgressByOther: return t('errors.room_in_progress_by_other')
        case Errors.OperationInProgress: return t('errors.operation_in_progress')
        case Errors.OperationMismatch:   return t('errors.operation_mismatch')
        // Said plainly rather than as "you have no right": nobody here can be given
        // this one. The full-screen notice (components/ModuleNotice.vue) is what the
        // person actually reads — this is only for a call that answered before it.
        case Errors.ModuleNotLicensed: return t('license.title')
        // Said as what it is — the card, not the person. "No rights" would send
        // somebody to their supervisor asking for a permission that would not help.
        case Errors.StaffNotEmployed:  return t('errors.staff_not_employed')
        // The rest of §9, each said as the one thing the person in front of it does
        // next. Their own sentences rather than the service's for the reason the one
        // above has one: the API's message is English.
        case Errors.StaffTimeSequence:      return t('errors.staff_time_sequence')
        case Errors.StaffTimeOutOfRange:    return t('errors.staff_time_out_of_range')
        case Errors.StaffBreakRecordingOff: return t('errors.staff_break_recording_off')
        case Errors.StaffSelfDecision:      return t('errors.staff_self_decision')
        case Errors.StaffPINAmbiguous:      return t('errors.staff_pin_ambiguous')
        case Errors.StaffPINThrottled:      return t('errors.staff_pin_throttled')
        case Errors.GroupHoldPickedUp:      return t('errors.group_hold_picked_up')
        default:                       return body?.message || fallback || t('errors.generic_error')
    }
}
